import { requireStringEnvVar } from "../../config";
import { getImpersonatedPersonId } from "../local-storage/impersonated-person";
import { getPreferredLanguage } from "../local-storage/language";
import { getOrganizationId } from "../local-storage/selected-organization";

export interface BffRequest {
  method?: "GET" | "POST";
  body?: string;
  headers?: Record<string, string>;
}

export class BffDownloadError extends Error {
  constructor(
    readonly status: number,
    readonly errorCode: string | null,
  ) {
    super(`Download failed with ${status}${errorCode ? ` (${errorCode})` : ""}`);
  }
}

const errorCodeOf = async (response: Response): Promise<string | null> => {
  try {
    const body = (await response.json()) as { errorCode?: string } | null;
    return body?.errorCode ?? null;
  } catch {
    return null;
  }
};

export interface BffFile {
  blob: Blob;
  fileName: string | null;
}

export const fetchBffBlob = async (path: string, request: BffRequest = {}): Promise<Blob> =>
  (await fetchBff(path, request)).blob();

export const fetchBffFile = async (path: string, request: BffRequest = {}): Promise<BffFile> => {
  const response = await fetchBff(path, request);
  return { blob: await response.blob(), fileName: attachmentName(response.headers.get("Content-Disposition")) };
};

const fetchBff = async (path: string, request: BffRequest): Promise<Response> => {
  const organizationId = getOrganizationId();
  const personId = getImpersonatedPersonId();
  const url = path.startsWith("http") ? path : `${requireStringEnvVar("VITE_BFF_BASE_URL")}${path}`;
  const response = await fetch(url, {
    method: request.method ?? "GET",
    body: request.body,
    credentials: "include",
    headers: {
      ...(organizationId ? { organizationId } : undefined),
      ...(personId ? { personId } : undefined),
      "preferred-language": getPreferredLanguage(),
      ...request.headers,
    },
  });
  if (!response.ok) throw new BffDownloadError(response.status, await errorCodeOf(response));
  return response;
};

const attachmentName = (disposition: string | null): string | null =>
  disposition?.match(/filename="([^"]+)"/)?.[1] ?? null;

export const saveBlob = (blob: Blob, fileName: string) => {
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  try {
    anchor.click();
  } finally {
    anchor.remove();
    window.URL.revokeObjectURL(objectUrl);
  }
};

export const downloadBffFile = async (path: string, fileName: string, request?: BffRequest) =>
  saveBlob(await fetchBffBlob(path, request), fileName);

export const jsonPost = (payload: unknown): BffRequest => ({
  method: "POST",
  body: JSON.stringify(payload),
  headers: { "Content-Type": "application/json" },
});
